import {
  Button,
  ButtonGroup,
  Column,
  FlexBox,
  HelpIcon,
  Icon,
  IconNames,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { ReactNode, useCallback, useMemo } from 'react';
import { useMeasure } from 'react-use';

import {
  MenuType,
  useScheduleContext,
} from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';

import styles from './listHeader.module.scss';

type Props = {
  headers: Header[];
  children?: ReactNode;
};

type Header = {
  label: string;
  subLabel?: string;
  helpText?: string;
};

const buttons: {
  icon: IconNames;
  name: string;
  text: string;
}[] = [
  {
    icon: 'clock-three',
    name: 'Business hours',
    text: 'Hours',
  },
  {
    icon: 'announcement',
    name: 'Audio messages',
    text: 'Audio messages',
  },
  {
    icon: 'reply',
    name: 'Auto-replies',
    text: 'Auto-replies',
  },
];
const menuItems: MenuType[] = ['hours', 'audioMessages', 'autoReplies'];

export const ListHeader = ({ children, headers }: Props): JSX.Element => {
  const { activeView, setActiveView } = useScheduleContext();
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const withButtonText = useMemo(() => width > 1000, [width]);

  const handleClick = useCallback(
    (type: MenuType) => {
      ampli.businessHoursScheduleViewClicked({
        tab: activeView.tab === 'businessHours' ? 'standard' : 'exception',
        view: type,
      });
      setActiveView({ ...activeView, menu: type });
    },
    [activeView, setActiveView],
  );

  const activeMenuIndex = menuItems.indexOf(activeView.menu);
  const {
    label: activeLabel,
    subLabel: activeSubLabel,
    helpText: activeHelpText,
  } = headers[activeMenuIndex];

  return (
    <Column.Row slottable noVerticalPadding noMinHeight ref={ref}>
      <Column.Slot placement="start">
        <FlexBox alignItems="center" gap="200">
          <Text.Heading variant="heading-3">{activeLabel}</Text.Heading>

          <Text.Paragraph variant="2">{activeSubLabel}</Text.Paragraph>

          {activeHelpText && (
            <HelpIcon dark title={activeLabel} text={activeHelpText} />
          )}
        </FlexBox>
      </Column.Slot>

      <Column.Slot placement="end">
        <FlexBox alignItems="center" gap="200">
          {children}

          <ButtonGroup className={styles.buttonGroup}>
            {buttons.map(({ icon, name, text }, index) => (
              <Button
                key={name}
                active={activeMenuIndex === index}
                className={styles.button}
                label={withButtonText ? text : ''}
                styleText={false}
                slotStart={
                  <Tooltip
                    key={name}
                    content={name}
                    disabled={withButtonText}
                    wrapProps={{ className: styles.iconTooltip }}
                    dark
                  >
                    <Icon
                      name={icon}
                      style={{ ...(withButtonText && { width: 32 }) }}
                    />
                  </Tooltip>
                }
                onClick={() => handleClick(menuItems[index])}
                variant="secondary"
              />
            ))}
          </ButtonGroup>
        </FlexBox>
      </Column.Slot>
    </Column.Row>
  );
};
